import React from 'react';
import { FileText, Upload, AlertCircle } from 'lucide-react';

export interface NoPdfAvailableProps {
  /** The invoice for context */
  invoice?: {
    id: string;
    invoice_number: string;
    pdf_filename?: string;
  };
  /** Optional callback for upload action */
  onUploadClick?: () => void;
  /** Whether upload functionality is available */
  canUpload?: boolean;
  /** Custom message override */
  message?: string;
  /** Show as error state vs empty state */
  isError?: boolean;
}

/**
 * NoPdfAvailable - A user-friendly component shown when PDF is not available
 * 
 * Provides clear messaging and optionally allows upload functionality
 * with proper visual hierarchy and call-to-action.
 */
export const NoPdfAvailable: React.FC<NoPdfAvailableProps> = ({
  invoice,
  onUploadClick,
  canUpload = false,
  message,
  isError = false,
}) => {
  const defaultMessage = isError 
    ? "Unable to load the PDF document"
    : invoice?.pdf_filename 
      ? "PDF document is currently unavailable"
      : "No PDF document has been uploaded for this invoice";

  const displayMessage = message || defaultMessage;
  const Icon = isError ? AlertCircle : FileText;
  const iconColor = isError ? "text-red-500" : "text-gray-400";
  const bgColor = isError ? "bg-red-50" : "bg-gray-50";

  return (
    <div className={`flex flex-col items-center justify-center h-full min-h-[400px] p-8 ${bgColor} rounded-lg border-2 border-dashed border-gray-300`}>
      {/* Icon */}
      <div className={`mb-4 ${iconColor}`}>
        <Icon size={64} strokeWidth={1.5} />
      </div>

      {/* Main message */}
      <h3 className={`text-lg font-medium mb-2 ${isError ? 'text-red-700' : 'text-gray-700'}`}>
        {displayMessage}
      </h3>

      {/* Context information */}
      {invoice && (
        <p className="text-sm text-gray-500 mb-6 text-center">
          Invoice: <span className="font-mono">{invoice.invoice_number}</span>
          {invoice.pdf_filename && (
            <>
              <br />
              Expected file: <span className="font-mono text-xs">{invoice.pdf_filename}</span>
            </>
          )}
        </p>
      )}

      {/* Action buttons */}
      <div className="flex flex-col items-center space-y-3">
        {canUpload && onUploadClick && (
          <button
            onClick={onUploadClick}
            className="flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg transition-colors duration-150 font-medium"
          >
            <Upload size={18} />
            <span>Upload PDF Document</span>
          </button>
        )}

        {isError && (
          <button
            onClick={() => window.location.reload()}
            className="text-sm text-gray-600 hover:text-gray-800 underline transition-colors duration-150"
          >
            Try reloading the page
          </button>
        )}

        {!isError && !canUpload && (
          <p className="text-xs text-gray-500 max-w-md text-center">
            The PDF document may be uploaded later or might be stored in a different location.
            Contact your administrator if you believe this is an error.
          </p>
        )}
      </div>
    </div>
  );
};
